import {createContext, type PropsWithChildren, use, useMemo, useState} from 'react'

import {defaultTitle, defaultTitleHeaderTag, type HeaderTag} from '../constants'

export type TitleContextProps = {
  /**
   * A title provides the ListView with a concise, descriptive name that communicates what kind of content it
   * contains. Will be visually hidden.
   */
  title: string

  /**
   * The HTML element used for the visually hidden title. Should change depending on the information hierarchy of the
   * page where the ListView is used.
   */
  titleHeaderTag: HeaderTag

  hasMetadataTitle: boolean
  setHasMetadataTitle: (hasMetadataTitle: boolean) => void
}

const TitleContext = createContext<TitleContextProps | undefined>(undefined)

export type TitleProviderProps = PropsWithChildren<{
  /**
   * The HTML element used for the visually hidden title. Should change depending on the information hierarchy of the
   * page where the ListView is used. Defaults to an `<h2>` tag.
   */
  titleHeaderTag?: TitleContextProps['titleHeaderTag']
}> &
  Pick<TitleContextProps, 'title'>

export const TitleProvider = ({children, title, titleHeaderTag = defaultTitleHeaderTag}: TitleProviderProps) => {
  const [hasMetadataTitle, setHasMetadataTitle] = useState(false)
  const contextProps = useMemo(
    () =>
      ({
        title: title.trim() || defaultTitle,
        titleHeaderTag,
        hasMetadataTitle,
        setHasMetadataTitle,
      }) satisfies TitleContextProps,
    [hasMetadataTitle, title, titleHeaderTag],
  )
  return <TitleContext value={contextProps}>{children}</TitleContext>
}

TitleProvider.displayName = 'ListViewTitleProvider'

export const useListViewTitle = () => {
  const context = use(TitleContext)
  if (!context) throw new Error('useListViewTitle must be used with TitleProvider.')
  return context
}
